import type { Page, RestReadContext } from '@rammetec/data-access'
import type { Invoice, InvoiceItem } from '@syscolect/domain'
import {
  extractMeta,
  extractRows,
  toInvoice,
  toInvoiceItem,
  type VhJobsResponse,
} from './restScriptVectorhub'

async function executeVhQuery<T>(
  ctx: RestReadContext,
  mapRow: (row: Record<string, unknown>) => T,
  handlerLabel: string,
): Promise<T[] | Page<T>> {
  const orgId = ctx.fixedWhere.org
  if (!orgId) {
    throw new Error(`[${handlerLabel}] RestScript getAll requires org scope (fixedWhere.org).`)
  }

  const queryId = `data-${ctx.tableName.toLowerCase()}`
  const { limit, offset, countTotal } = ctx.options ?? {}
  const page = limit != null ? { limit, offset: offset ?? 0 } : undefined

  const res = await ctx.request<VhJobsResponse>('POST', '/jobs', {
    orgId,
    action: 'data.execute',
    params: {
      queryId,
      ...(page ? { page } : {}),
      ...(countTotal ? { countTotal: true } : {}),
    },
    sync: true,
    syncTimeout: 30_000,
  })

  const items = extractRows(res.job).map(mapRow)

  if (!page && !countTotal) return items

  const meta = extractMeta(res.job)
  return {
    items,
    hasMore: meta.hasMore ?? false,
    total: meta.totalCount,
    offset: meta.offset ?? page?.offset ?? 0,
  }
}

/**
 * RestScript `getAll` handler for Invoice — VectorHub `data-invoice`.
 */
export async function invoiceRestScriptGetAll(
  ctx: RestReadContext,
): Promise<Invoice[] | Page<Invoice>> {
  if (ctx.tableName !== 'Invoice') return []
  return executeVhQuery(ctx, toInvoice, 'restScriptInvoiceHandler')
}

/**
 * RestScript `getAll` handler for InvoiceItem — VectorHub `data-invoiceitem`.
 */
export async function invoiceItemRestScriptGetAll(
  ctx: RestReadContext,
): Promise<InvoiceItem[] | Page<InvoiceItem>> {
  if (ctx.tableName !== 'InvoiceItem') return []
  return executeVhQuery(ctx, toInvoiceItem, 'restScriptInvoiceHandler')
}
